import type { AxiosResponse } from 'axios';
import API from './axios';

/** Public: submit unlock request (no auth). */
export const requestUnlockApiCall = async (payload: {
  token: string;
  email: string;
  reason?: string;
}): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return API.post('api/auth/request-unlock', payload);
};

/** Super admin: list locked accounts. */
export const getLockedAccountsApiCall = async (): Promise<AxiosResponse<LockedAccount[]>> => {
  return API.get('api/admin/locked-accounts');
};

/** Super admin: list pending unlock requests. */
export const getUnlockRequestsApiCall = async (): Promise<
  AxiosResponse<UnlockRequestRecord[]>
> => {
  return API.get('api/admin/unlock-requests');
};

/** Super admin: pending unlock requests count. */
export const getUnlockRequestsCountApiCall = async (): Promise<AxiosResponse<number>> => {
  return API.get('api/admin/unlock-requests/count');
};

/** Super admin: unlock account directly. */
export const unlockAccountApiCall = async (
  userId: number,
  body?: { reason?: string }
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return API.post(`api/admin/unlock-account/${userId}`, body ?? {});
};

/** Super admin: approve unlock request. */
export const approveUnlockRequestApiCall = async (
  requestId: number,
  body?: { responseReason?: string }
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return API.post(`api/admin/unlock-request/${requestId}/approve`, body ?? {});
};

/** Super admin: reject unlock request (responseReason required). */
export const rejectUnlockRequestApiCall = async (
  requestId: number,
  body: { responseReason: string }
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  return API.post(`api/admin/unlock-request/${requestId}/reject`, body);
};

export interface LockedAccount {
  UserID: number;
  FirstName: string;
  LastName?: string;
  ContactEmail?: string;
  ContactPhone?: string;
  AccessLevel?: number;
  OrganizationID?: number;
  IsLocked: true;
  LockedAt: string;
  LockedReason?: string;
  LastActivityDate?: string | null;
  LastWarningEmailSentAt?: string | null;
  groups?: Array<{ GroupID: number; GroupName: string }>;
}

export type UnlockRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface UnlockRequestRecord {
  RequestID: number;
  UserID: number;
  RequestToken: string;
  Status: UnlockRequestStatus;
  RequestReason?: string;
  ResponseReason?: string | null;
  CreatedAt: string;
  ProcessedAt?: string | null;
  ProcessedByUserID?: number | null;
  user?: LockedAccount;
  processedBy?: unknown;
}
